import React, { useEffect, useMemo, useState, useRef } from 'react';
import { FileTextIcon, DownloadIcon, SearchIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, InboxIcon } from 'lucide-react';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useRole } from '../hooks/useRole';
const mockPatientReports = [{
  id: 'REF-001',
  type: 'Risonanza magnetica encefalo',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-10',
  time: '09:30',
  status: 'Consegnato'
}, {
  id: 'REF-002',
  type: 'TAC torace',
  doctor: 'Dr. A. Neri',
  date: '2026-02-18',
  time: '11:00',
  status: 'Consegnato'
}, {
  id: 'REF-003',
  type: 'Ecografia addome',
  doctor: 'Dr. M. Gialli',
  date: '2026-01-22',
  time: '14:30',
  status: 'Consegnato'
}, {
  id: 'REF-004',
  type: 'Radiografia colonna vertebrale',
  doctor: 'Dr. S. Verdi',
  date: '2025-12-05',
  time: '10:00',
  status: 'Consegnato'
}, {
  id: 'REF-005',
  type: 'Risonanza magnetica ginocchio',
  doctor: 'Dr. A. Neri',
  date: '2025-11-15',
  time: '08:30',
  status: 'Consegnato'
}, {
  id: 'REF-006',
  type: 'TAC addome',
  doctor: 'Dr. M. Gialli',
  date: '2025-10-20',
  time: '16:00',
  status: 'Consegnato'
}, {
  id: 'REF-007',
  type: 'Ecografia pelvi',
  doctor: 'Dr. S. Verdi',
  date: '2025-09-12',
  time: '09:00',
  status: 'Consegnato'
}, {
  id: 'REF-008',
  type: 'Radiografia torace',
  doctor: 'Dr. A. Neri',
  date: '2025-08-03',
  time: '11:30',
  status: 'Consegnato'
}];
const uniqueExamTypes = Array.from(new Set(mockPatientReports.map(r => r.type))).sort();
function TruncatedCell({
  text
}: {
  text: string;
}) {
  const [show, setShow] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const isMobileView = useIsMobile();
  const handleMouseEnter = () => {
    if (isMobileView) return;
    if (textRef.current && textRef.current.scrollWidth > textRef.current.clientWidth) {
      setShow(true);
    }
  };
  return <div className="relative min-w-0" onMouseEnter={handleMouseEnter} onMouseLeave={() => setShow(false)} data-id="element-1855">
      <span ref={textRef} className="block truncate" data-id="element-1856">
        {text}
      </span>
      {show && !isMobileView && <div className="fixed px-2.5 py-1.5 bg-text-primary text-white text-xs rounded-md whitespace-nowrap pointer-events-none shadow-lg z-[9999]" style={{
      transform: 'translateY(-100%) translateY(-6px)'
    }} ref={el => {
      if (el && el.parentElement) {
        const parent = el.parentElement.getBoundingClientRect();
        el.style.top = `${parent.top}px`;
        el.style.left = `${parent.left}px`;
      }
    }} data-id="element-1857">
          {text}
        </div>}
    </div>;
}
export function PatientReports() {
  const isMobile = useIsMobile();
  const {
    role
  } = useRole();
  const isPaziente = role === 'paziente';
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const [typeFilter, setTypeFilter] = useState('Tutti');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const hasReports = mockPatientReports.length > 0;
  const latestReport = hasReports ? mockPatientReports[0] : null;
  const processedReports = useMemo(() => {
    let result = [...mockPatientReports];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => r.type.toLowerCase().includes(q) || r.doctor.toLowerCase().includes(q) || r.date.includes(q));
    }
    if (statusFilter !== 'Tutti') {
      result = result.filter(r => r.status === statusFilter);
    }
    if (typeFilter !== 'Tutti') {
      result = result.filter(r => r.type === typeFilter);
    }
    return result;
  }, [searchQuery, statusFilter, typeFilter]);
  const totalPages = Math.ceil(processedReports.length / itemsPerPage);
  const paginatedReports = processedReports.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, typeFilter]);
  // Full empty state when no reports exist at all
  if (!hasReports) {
    return <div className="space-y-8 relative w-full min-w-0" data-id="element-1858">
        <div data-id="element-1859">
          <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-1860">
            I miei referti
          </h1>
          <p className="text-sm md:text-body text-text-secondary" data-id="element-1861">
            Consulta e scarica i tuoi referti medici.
          </p>
        </div>
        <div className="medical-card p-10 md:p-16 flex flex-col items-center justify-center text-center" data-id="element-1862">
          <div className="w-16 h-16 rounded-full bg-clinical-gray-100 flex items-center justify-center mb-5" data-id="element-1863">
            <InboxIcon size={32} className="text-text-tertiary" data-id="element-1864" />
          </div>
          <h3 className="text-lg font-semibold text-text-primary mb-2" data-id="element-1865">
            Non hai ancora referti disponibili
          </h3>
          <p className="text-sm text-text-secondary max-w-md" data-id="element-1866">
            I tuoi referti compariranno qui dopo che il medico avrà completato
            la refertazione.
          </p>
        </div>
      </div>;
  }
  return <div className="space-y-8 relative w-full min-w-0" data-id="element-1867">
      {/* Header */}
      <div data-id="element-1868">
        <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-1869">
          I miei referti
        </h1>
        <p className="text-sm md:text-body text-text-secondary" data-id="element-1870">
          Consulta e scarica i tuoi referti medici.
        </p>
      </div>

      {/* Latest Report Highlight */}
      {latestReport && <section data-id="element-1871">
          <div className="medical-card p-5 md:p-6 border-l-4 border-l-medical-green" data-id="element-1872">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1873">
              <div className="flex items-start gap-4" data-id="element-1874">
                <div className="w-12 h-12 rounded-full bg-medical-green-50 text-medical-green flex items-center justify-center shrink-0 mt-1 md:mt-0" data-id="element-1875">
                  <FileTextIcon size={24} data-id="element-1876" />
                </div>
                <div data-id="element-1877">
                  <span className="text-xs font-semibold text-medical-green uppercase tracking-wider mb-1 block" data-id="element-1878">
                    Ultimo referto disponibile
                  </span>
                  <h3 className="text-lg md:text-xl font-bold text-text-primary mb-1" data-id="element-1879">
                    {latestReport.type}
                  </h3>
                  <p className="text-sm text-text-secondary" data-id="element-1880">
                    {latestReport.date} alle {latestReport.time} •{' '}
                    {latestReport.doctor}
                  </p>
                </div>
              </div>
              <button className="medical-btn-primary w-full md:w-auto shrink-0" data-id="element-1881">
                <DownloadIcon size={18} data-id="element-1882" /> Scarica referto PDF
              </button>
            </div>
          </div>
        </section>}

      {/* History Section */}
      <section className="min-w-0" data-id="element-1883">
        <h2 className="text-xl md:text-section-title mb-4 text-text-primary font-semibold" data-id="element-1884">
          Storico referti
        </h2>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4 bg-white p-4 rounded-medical-lg shadow-sm border border-clinical-gray-100" data-id="element-1885">
          <div className={`relative ${isPaziente ? 'w-full sm:w-56' : 'w-full sm:flex-1'}`} data-id="element-1886">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1887" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca per esame o medico..." className="medical-input pl-9 text-sm h-9 w-full" data-id="element-1888" />
          </div>
          <div className="relative w-full sm:w-auto" data-id="element-1889">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full sm:w-auto" data-id="element-1890">
              <option value="Tutti" data-id="element-1891">Stato: Tutti</option>
              <option value="Consegnato" data-id="element-1892">Consegnato</option>
            </select>
            <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1893" />
          </div>
          <div className="relative w-full sm:w-auto" data-id="element-1894">
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full sm:w-auto" data-id="element-1895">
              <option value="Tutti" data-id="element-1896">Tipo esame: Tutti</option>
              {uniqueExamTypes.map(t => <option key={t} value={t} data-id="element-1897">
                  {t}
                </option>)}
            </select>
            <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1898" />
          </div>
        </div>

        {/* Table / Cards */}
        {!isMobile ? <div className="w-full bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 overflow-x-auto" data-id="element-1899">
            <table className="w-full text-left border-collapse" style={{
          tableLayout: 'fixed'
        }} data-id="element-1900">
              <colgroup data-id="element-1901">
                <col style={{
              width: '35%'
            }} data-id="element-1902" />
                <col style={{
              width: '20%'
            }} data-id="element-1903" />
                <col style={{
              width: '20%'
            }} data-id="element-1904" />
                <col style={{
              width: '15%'
            }} data-id="element-1905" />
                <col style={{
              width: '10%'
            }} data-id="element-1906" />
              </colgroup>
              <thead data-id="element-1907">
                <tr className="bg-clinical-gray border-b border-clinical-gray-200" data-id="element-1908">
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1909">
                    Tipo Esame
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1910">
                    Medico
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1911">
                    Data e ora
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1912">
                    Stato
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap text-right" data-id="element-1913">
                    Azione
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1914">
                {paginatedReports.length === 0 ? <tr data-id="element-1915">
                    <td colSpan={5} className="px-4 py-8 text-center text-text-secondary text-body" data-id="element-1916">
                      Nessun referto trovato.
                    </td>
                  </tr> : paginatedReports.map(item => <tr key={item.id} className="border-b border-clinical-gray-100 last:border-0 hover:bg-clinical-gray/30 transition-colors" data-id="element-1917">
                      <td className="px-4 py-3 text-body font-medium text-text-primary overflow-hidden" data-id="element-1918">
                        <TruncatedCell text={item.type} data-id="element-1919" />
                      </td>
                      <td className="px-4 py-3 text-body text-text-secondary overflow-hidden" data-id="element-1920">
                        <TruncatedCell text={item.doctor} data-id="element-1921" />
                      </td>
                      <td className="px-4 py-3 text-body text-text-secondary" data-id="element-1922">
                        {item.date} • {item.time}
                      </td>
                      <td className="px-4 py-3" data-id="element-1923">
                        <StatusBadge status={item.status} data-id="element-1924" />
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-1925">
                        <button className="p-1.5 text-medical-green hover:bg-medical-green-50 rounded transition-colors inline-flex" title="Scarica PDF" data-id="element-1926">
                          <DownloadIcon size={18} data-id="element-1927" />
                        </button>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div> : <div className="space-y-3" data-id="element-1928">
            {paginatedReports.length === 0 ? <div className="p-6 text-center text-text-secondary text-body bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" data-id="element-1929">
                Nessun referto trovato.
              </div> : paginatedReports.map(item => <div key={item.id} className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-4 space-y-3" data-id="element-1930">
                  <div className="flex justify-between items-start" data-id="element-1931">
                    <div className="min-w-0 mr-3" data-id="element-1932">
                      <span className="text-sm font-medium text-text-primary block mb-0.5 truncate" data-id="element-1933">
                        {item.type}
                      </span>
                      <span className="text-xs text-text-secondary block truncate" data-id="element-1934">
                        {item.doctor}
                      </span>
                    </div>
                    <StatusBadge status={item.status} data-id="element-1935" />
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-clinical-gray-100" data-id="element-1936">
                    <span className="text-xs text-text-tertiary" data-id="element-1937">
                      {item.date} alle {item.time}
                    </span>
                    <button className="flex items-center gap-1.5 text-sm text-medical-green font-medium" data-id="element-1938">
                      <DownloadIcon size={16} data-id="element-1939" /> Scarica
                    </button>
                  </div>
                </div>)}
          </div>}

        {/* Pagination */}
        {totalPages > 1 && <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 px-1 gap-2" data-id="element-1940">
            <span className="text-sm text-text-secondary" data-id="element-1941">
              {processedReports.length} risultati — Pagina {currentPage} di{' '}
              {totalPages}
            </span>
            <div className="flex items-center gap-2" data-id="element-1942">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1943">
                <ChevronLeftIcon size={16} data-id="element-1944" />
              </button>
              <div className="flex items-center gap-1" data-id="element-1945">
                {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-colors ${currentPage === page ? 'bg-medical-green text-white border border-medical-green' : 'border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50'}`} data-id="element-1946">
                    {page}
                  </button>)}
              </div>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1947">
                <ChevronRightIcon size={16} data-id="element-1948" />
              </button>
            </div>
          </div>}
      </section>
    </div>;
}